import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { selectIdentity } from "../../../selectors/userSelectors";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import {
  fetchCompany,
  fetchMembers,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
  sameMembershipGeneration,
} from "../../companyWorkspace/lib/scopeSession";
import { memberDisplayName } from "../../companyWork/lib/workLabels";
import {
  isRequestAbortError,
  reassignDealRepresentative,
} from "../api/deals";
import { canShowRepresentativeReassignCta } from "../lib/dealSidePresentation";
import { partyDisplayName } from "../lib/dealFormatting";

function unwrapMembers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function membershipGenerationOf(company) {
  const membership = company && company.my_membership;
  if (!membership) return null;
  return membership.membership_generation ?? null;
}

function memberUserId(member) {
  if (!member) return null;
  if (member.user && member.user.id != null) return member.user.id;
  if (member.user_id != null) return member.user_id;
  return null;
}

/**
 * Representative reassignment for one company Deal side.
 * CTA visibility is UX-only; backend remains authoritative.
 */
function DealRepresentativeReassign({
  deal,
  presentation,
  routeDealId,
  onDealUpdated,
  onDealInaccessible,
}) {
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const submitInFlightRef = useRef(false);

  const [company, setCompany] = useState(null);
  const [companyState, setCompanyState] = useState("idle");
  const [panelOpen, setPanelOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersState, setMembersState] = useState("idle");
  const [membersError, setMembersError] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(null);
  const [conflictMessage, setConflictMessage] = useState(null);

  const companyId = presentation?.companyId ?? null;
  const dealId = deal?.id ?? null;
  const generation = membershipGenerationOf(company);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      submitInFlightRef.current = false;
    };
  }, [scopeGate]);

  useEffect(() => {
    // Only bind membershipGeneration while a company projection is loaded.
    // Binding `null` after a company clear would bump lifetime and invalidate
    // the in-flight company fetch that must establish the next generation.
    if (company != null) {
      scopeGate.bindIdentity({
        companyId,
        actorId,
        resourceId: dealId,
        membershipGeneration: generation,
        bindMembershipGeneration: true,
        bindResourceId: true,
      });
      return;
    }
    scopeGate.bindIdentity({
      companyId,
      actorId,
      resourceId: dealId,
      bindMembershipGeneration: false,
      bindResourceId: true,
    });
  }, [scopeGate, companyId, actorId, dealId, generation, company]);

  useEffect(() => {
    setPanelOpen(false);
    setMembers([]);
    setMembersState("idle");
    setMembersError(null);
    setSelectedId("");
    setFormError(null);
    setFieldErrors(null);
    setConflictMessage(null);
    setCompany(null);
    setCompanyState("idle");
    submitInFlightRef.current = false;
  }, [dealId, companyId, actorId, routeDealId]);

  useEffect(() => {
    if (
      !companyId
      || !deal
      || !presentation
      || !presentation.isViewerSide
      || presentation.kind !== "company"
    ) {
      setCompany(null);
      setCompanyState("idle");
      return undefined;
    }

    const controller = new AbortController();
    const op = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: dealId,
    });
    setCompanyState("loading");

    fetchCompany(companyId)
      .then((data) => {
        if (controller.signal.aborted) return;
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        if (
          !sameCompanyId(data?.id, companyId)
          || String(routeDealId) !== String(dealId)
        ) {
          return;
        }
        setCompany(data);
        setCompanyState("loaded");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setCompany(null);
        setCompanyState("error");
      });

    return () => {
      controller.abort();
    };
  }, [
    scopeGate,
    companyId,
    actorId,
    dealId,
    deal,
    presentation,
    routeDealId,
  ]);

  const showCta = canShowRepresentativeReassignCta({
    presentation,
    company,
    deal,
  });

  const loadMembers = () => {
    if (!companyId || !showCta) return;
    const op = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: dealId,
      membershipGeneration: generation,
    });
    setMembersState("loading");
    setMembersError(null);
    fetchMembers(companyId, "active")
      .then((data) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const list = unwrapMembers(data).filter((member) => {
          const status = member?.status || "active";
          return status === "active" && memberUserId(member) != null;
        });
        setMembers(list);
        setMembersState("loaded");
      })
      .catch((error) => {
        if (isRequestAbortError(error)) return;
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setMembersError(messageFromError(error));
        setMembersState("error");
      });
  };

  const handleOpenPanel = () => {
    setPanelOpen(true);
    setFormError(null);
    setFieldErrors(null);
    setConflictMessage(null);
    const currentRepId =
      presentation?.representative && presentation.representative.id != null
        ? String(presentation.representative.id)
        : "";
    setSelectedId(currentRepId);
    loadMembers();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Synchronous duplicate guard — claimed before React commits busy UI.
    if (submitInFlightRef.current || submitting) return;
    if (!showCta || !dealId || !presentation?.side) return;

    const representativeId = Number.parseInt(String(selectedId), 10);
    if (!Number.isFinite(representativeId) || representativeId < 1) {
      setFormError("Оберіть активного учасника компанії.");
      return;
    }

    submitInFlightRef.current = true;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: dealId,
      membershipGeneration: generation,
    });
    setSubmitting(true);
    setFormError(null);
    setFieldErrors(null);
    setConflictMessage(null);

    const ownedDealId = String(dealId);
    const ownedCompanyId = String(companyId);
    const ownedActorId = actorId;
    const ownedGeneration = generation;

    try {
      const updated = await reassignDealRepresentative(dealId, {
        side: presentation.side,
        representativeId,
      });

      const stillValid =
        scopeGate.mutationStillValid(op, { mounted: mountedRef.current })
        && String(routeDealId) === ownedDealId
        && sameCompanyId(ownedCompanyId, companyId)
        && sameActorId(ownedActorId, actorId)
        && sameMembershipGeneration(ownedGeneration, generation);

      if (!stillValid) return;

      if (typeof onDealUpdated === "function") {
        onDealUpdated(updated);
      }
      setPanelOpen(false);
      setConflictMessage(null);
    } catch (error) {
      const stillValid =
        scopeGate.mutationStillValid(op, { mounted: mountedRef.current })
        && String(routeDealId) === ownedDealId;

      if (!stillValid) return;

      const status = error && error.response && error.response.status;
      if (status === 404) {
        if (typeof onDealInaccessible === "function") {
          onDealInaccessible();
        }
        return;
      }
      if (status === 409) {
        setConflictMessage(
          "Дані угоди змінилися. Оновіть сторінку або повторіть після оновлення.",
        );
        if (typeof onDealUpdated === "function") {
          // Caller refetches authoritative Deal; do not invent state.
          onDealUpdated(null, { conflict: true });
        }
        return;
      }
      if (status === 400) {
        const fields = parseDrfFieldErrors(error, [
          "side",
          "representative_id",
          "detail",
          "non_field_errors",
        ]);
        setFieldErrors(fields);
        setFormError(fields._form || messageFromError(error));
        return;
      }
      setFormError(messageFromError(error));
    } finally {
      if (
        submitInFlightRef.current
        && String(routeDealId) === ownedDealId
      ) {
        submitInFlightRef.current = false;
      }
      if (
        mountedRef.current
        && scopeGate.mutationStillValid(op, { mounted: true })
        && String(routeDealId) === ownedDealId
      ) {
        setSubmitting(false);
      } else if (mountedRef.current && String(routeDealId) === ownedDealId) {
        setSubmitting(false);
      }
    }
  };

  if (!presentation || presentation.kind !== "company") return null;
  if (companyState === "loading") {
    return (
      <div
        className="feature-deal-detail__reassign"
        data-testid={`deal-reassign-loading-${presentation.side}`}
      >
        <Spinner size="sm" label="Перевірка повноважень" />
      </div>
    );
  }
  if (!showCta) return null;

  return (
    <div
      className="feature-deal-detail__reassign"
      data-testid={`deal-reassign-${presentation.side}`}
    >
      {!panelOpen ? (
        <button
          type="button"
          className="action-btn"
          onClick={handleOpenPanel}
          data-testid={`deal-reassign-open-${presentation.side}`}
        >
          Змінити представника
        </button>
      ) : (
        <form
          className="feature-deal-detail__reassign-form"
          onSubmit={handleSubmit}
          data-testid={`deal-reassign-form-${presentation.side}`}
        >
          <label className="feature-deal-detail__reassign-label">
            <span>Новий представник</span>
            {membersState === "loading" ? (
              <Spinner size="sm" label="Завантаження учасників" />
            ) : null}
            {membersState === "error" ? (
              <p className="feature-deal-detail__status-error" role="alert">
                {membersError || "Не вдалося завантажити учасників."}
                {" "}
                <button
                  type="button"
                  className="action-btn"
                  onClick={loadMembers}
                >
                  Спробувати знову
                </button>
              </p>
            ) : null}
            {membersState === "loaded" ? (
              <select
                className="feature-deals-list__select"
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value)}
                disabled={submitting}
                aria-label="Новий представник компанії"
                data-testid={`deal-reassign-select-${presentation.side}`}
              >
                <option value="">Оберіть учасника</option>
                {members.map((member) => {
                  const uid = memberUserId(member);
                  const label =
                    memberDisplayName(member)
                    || partyDisplayName(member.user)
                    || `Учасник #${uid}`;
                  return (
                    <option key={String(uid)} value={String(uid)}>
                      {label}
                    </option>
                  );
                })}
              </select>
            ) : null}
          </label>
          {fieldErrors && fieldErrors.representative_id ? (
            <p className="feature-deal-detail__status-error" role="alert">
              {fieldErrors.representative_id}
            </p>
          ) : null}
          {fieldErrors && fieldErrors.side ? (
            <p className="feature-deal-detail__status-error" role="alert">
              {fieldErrors.side}
            </p>
          ) : null}
          {formError ? (
            <p className="feature-deal-detail__status-error" role="alert">
              {formError}
            </p>
          ) : null}
          {conflictMessage ? (
            <p className="feature-deal-detail__status-error" role="alert">
              {conflictMessage}
            </p>
          ) : null}
          <div className="feature-deal-detail__reassign-actions">
            <button
              type="submit"
              className="action-btn"
              disabled={submitting || membersState !== "loaded"}
              data-testid={`deal-reassign-submit-${presentation.side}`}
            >
              {submitting ? "Збереження…" : "Зберегти"}
            </button>
            <button
              type="button"
              className="action-btn"
              disabled={submitting}
              onClick={() => setPanelOpen(false)}
            >
              Скасувати
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default DealRepresentativeReassign;
